import { ERROR_CODES } from '@cercle/shared';
import type { QueueStorage } from './idb';
import { operationKey, type QueueEntry, type QueuedOperation, type SyncStatus } from './types';

/**
 * File d'attente hors-ligne (US-10 : « le wifi de la salle Courbet est peu fiable »).
 *
 * Trois propriétés qui comptent, dans l'ordre :
 *
 *  1. **Déduplication par clé métier.** Le membre bouge ses curseurs vingt fois avant de
 *     valider ; on ne veut pas vingt requêtes en file mais une seule, la dernière.
 *  2. **Idempotence du rejeu.** Toutes les opérations sont des `PUT` identifiés par
 *     (séance, rhum, membre) : un rejeu accidentel n'a aucun effet observable.
 *  3. **Last-write-wins arbitré par le serveur.** Une entrée rejouée que le serveur juge
 *     périmée (409 + STALE_WRITE) est **abandonnée**, pas retentée. Sans cela, une note
 *     saisie hors-ligne à 18h05 écraserait indéfiniment une correction faite en ligne
 *     à 18h30, et la file ne se viderait jamais.
 */

export type SendResult =
  | { outcome: 'sent' }
  /** Le serveur a refusé définitivement : on retire l'entrée sans la rejouer. */
  | { outcome: 'rejected'; reason: string }
  /** Le serveur a répondu en erreur (5xx) : l'entrée reste en file, la tentative compte. */
  | { outcome: 'retry' }
  /**
   * Aucune réponse : le réseau manque. L'entrée reste en file **sans** que la tentative
   * compte — en salle Courbet, cinq coupures ne doivent pas suffire à jeter une note — et
   * la vague s'arrête là : attendre l'expiration de chaque envoi suivant n'apprendrait rien.
   */
  | { outcome: 'offline' };

export type Sender = (operation: QueuedOperation) => Promise<SendResult>;

/** Au-delà, l'entrée est abandonnée : la garder indéfiniment ferait grossir la file sans fin. */
export const MAX_ATTEMPTS = 5;

export interface OfflineQueueOptions {
  storage: QueueStorage;
  send: Sender;
  /** Injectée pour que les tests n'aient pas à dépendre de l'heure réelle. */
  now?: () => Date;
  onChange?: (status: SyncStatus) => void;
}

export class OfflineQueue {
  private readonly storage: QueueStorage;
  private readonly send: Sender;
  private readonly now: () => Date;
  private readonly onChange: (status: SyncStatus) => void;

  private flushing: Promise<void> | null = null;
  private flushAgain = false;
  private discarded = 0;
  private lastSyncedAt: string | null = null;

  constructor(options: OfflineQueueOptions) {
    this.storage = options.storage;
    this.send = options.send;
    this.now = options.now ?? (() => new Date());
    this.onChange = options.onChange ?? (() => {});
  }

  /**
   * Met une écriture en file. Si une entrée existe déjà pour la même clé métier, elle est
   * remplacée — sauf si celle en file est plus récente, auquel cas la nouvelle est ignorée
   * (le même arbitrage que côté serveur, appliqué en local pour ne pas envoyer un périmé).
   */
  async enqueue(operation: QueuedOperation): Promise<void> {
    const id = operationKey(operation);
    const clientUpdatedAt = operation.payload.clientUpdatedAt;

    const existing = (await this.storage.all()).find((entry) => entry.id === id);
    if (existing && existing.clientUpdatedAt > clientUpdatedAt) return;

    await this.storage.put({
      id,
      operation,
      clientUpdatedAt,
      attempts: 0,
      queuedAt: existing?.queuedAt ?? this.now().toISOString(),
    });

    await this.notify();
  }

  /**
   * Rejoue la file. Sûre à appeler en concurrence : un second appel pendant un envoi
   * en cours n'ouvre pas une deuxième vague — la reconnexion et le retour de l'onglet
   * au premier plan déclenchent souvent l'événement en même temps.
   *
   * Il ne repart pas pour autant les mains vides : il attend la vague en cours et en
   * réclame une de plus à la fin. Sans cela, une note validée pendant qu'une autre partait
   * restait en file jusqu'au prochain retour réseau — « en attente » sans raison visible.
   */
  flush(): Promise<void> {
    if (this.flushing) {
      this.flushAgain = true;
      return this.flushing;
    }
    this.flushing = this.run().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async run(): Promise<void> {
    await this.notify('syncing');
    try {
      do {
        this.flushAgain = false;
        if (!(await this.wave())) return;
      } while (this.flushAgain);
      this.lastSyncedAt = this.now().toISOString();
    } finally {
      this.flushAgain = false;
      await this.notify();
    }
  }

  /** Une passe sur la file. Renvoie false si le réseau a manqué en route. */
  private async wave(): Promise<boolean> {
    for (const entry of await this.storage.all()) {
      if (!(await this.attempt(entry))) return false;
    }
    return true;
  }

  private async attempt(entry: QueueEntry): Promise<boolean> {
    let result: SendResult;
    try {
      result = await this.send(entry.operation);
    } catch {
      // Une exception du transport, c'est l'absence de réseau : on retente plus tard.
      result = { outcome: 'offline' };
    }

    if (result.outcome === 'offline') return false;

    if (result.outcome === 'sent') {
      await this.settle(entry, null);
      return true;
    }

    if (result.outcome === 'rejected') {
      // Le serveur détient mieux, ou l'écriture est illégale (séance close). Insister
      // ne changerait rien : on abandonne et l'UI le signale.
      if (await this.settle(entry, null)) this.discarded += 1;
      return true;
    }

    const attempts = entry.attempts + 1;
    if (attempts >= MAX_ATTEMPTS) {
      if (await this.settle(entry, null)) this.discarded += 1;
      return true;
    }
    await this.settle(entry, { ...entry, attempts });
    return true;
  }

  /**
   * Retire (`null`) ou met à jour l'entrée envoyée — à condition qu'elle n'ait pas été
   * remplacée pendant l'envoi. Le membre a pu réenregistrer sa note entretemps : la
   * nouvelle version porte la même clé, et l'effacer sur la foi de l'ancienne la
   * perdrait sans bruit. Renvoie false si l'entrée a été remplacée.
   */
  private async settle(sent: QueueEntry, next: QueueEntry | null): Promise<boolean> {
    const current = await this.storage.get(sent.id);
    if (current && current.clientUpdatedAt !== sent.clientUpdatedAt) return false;
    if (next) await this.storage.put(next);
    else await this.storage.remove(sent.id);
    return true;
  }

  async pendingCount(): Promise<number> {
    return (await this.storage.all()).length;
  }

  async pending(): Promise<QueueEntry[]> {
    return this.storage.all();
  }

  /** L'opération encore en file sous cette clé métier, s'il y en a une. */
  async find(key: string): Promise<QueuedOperation | undefined> {
    return (await this.storage.get(key))?.operation;
  }

  async clear(): Promise<void> {
    await this.storage.clear();
    this.discarded = 0;
    await this.notify();
  }

  /** Remet à zéro le compteur d'abandons une fois l'utilisateur averti. */
  acknowledgeDiscarded(): void {
    this.discarded = 0;
  }

  async status(): Promise<SyncStatus> {
    return this.computeStatus(this.flushing ? 'syncing' : undefined);
  }

  private async computeStatus(forced?: SyncStatus['state']): Promise<SyncStatus> {
    const pending = await this.pendingCount();
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;

    let state: SyncStatus['state'];
    if (forced) state = forced;
    else if (!online) state = 'offline';
    else if (this.discarded > 0) state = 'error';
    else state = 'idle';

    return { state, pending, discarded: this.discarded, lastSyncedAt: this.lastSyncedAt };
  }

  private async notify(forced?: SyncStatus['state']): Promise<void> {
    this.onChange(await this.computeStatus(forced));
  }
}

/**
 * Traduit une réponse HTTP en verdict pour la file.
 *
 * Le partage du code d'erreur avec l'API (`@cercle/shared`) évite d'avoir à deviner :
 * un 409 STALE_WRITE et un 423 SESSION_CLOSED sont des refus définitifs, un 5xx ou une
 * absence de réseau sont des échecs temporaires.
 *
 * SESSION_NOT_OPEN est traité comme un refus définitif et non comme un rejeu à retenter :
 * les écrans n'ouvrent le formulaire que sur une séance lancée, une entrée en file pour une
 * séance non lancée est donc déjà une anomalie, et la garder n'y changerait rien.
 */
export function verdictForResponse(status: number, code?: string): SendResult {
  if (status >= 200 && status < 300) return { outcome: 'sent' };

  if (code === ERROR_CODES.STALE_WRITE) {
    return { outcome: 'rejected', reason: 'Une version plus récente existe sur le serveur.' };
  }
  if (code === ERROR_CODES.SESSION_CLOSED) {
    return { outcome: 'rejected', reason: 'La séance a été clôturée entretemps.' };
  }
  if (code === ERROR_CODES.SESSION_NOT_OPEN) {
    return { outcome: 'rejected', reason: 'La séance n’était pas encore lancée.' };
  }
  if (code === ERROR_CODES.GUESS_LOCKED) {
    return { outcome: 'rejected', reason: 'Le pronostic était déjà verrouillé.' };
  }

  // 401 malgré le renouvellement : la session est perdue, pas la note. Elle attend en
  // file que le membre se reconnecte, comme elle attendrait le réseau.
  if (status === 401) return { outcome: 'offline' };

  // 4xx : la requête est mauvaise, la rejouer telle quelle échouera pareil.
  if (status >= 400 && status < 500) {
    return { outcome: 'rejected', reason: 'Cette saisie a été refusée par le serveur.' };
  }

  // Statut 0 : aucune réponse, le wifi de la salle Courbet a encore lâché.
  if (status === 0) return { outcome: 'offline' };

  // 5xx : le serveur est peut-être seulement en train de redémarrer.
  return { outcome: 'retry' };
}
